import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/** Hidden line-item property — only lines added by this script are auto-removed when below threshold. */
const GWP_AUTO_PROPERTY = '_saltlab_gwp';
const GWP_AUTO_VALUE = '1';

/** One document-level hook: hold checkout until GWP cart lines are synced. */
let cartProgressCheckoutHookInstalled = false;

/**
 * Checkout is often a submit button outside the cart <form> (associated via
 * form="cart-form"). querySelector on the form never finds it — without this,
 * post-sync requestSubmit never runs and customers cannot checkout.
 * @param {HTMLFormElement | HTMLElement | null} form
 * @returns {HTMLButtonElement | null}
 */
function cartProgressFindCheckoutButton(form) {
  if (!(form instanceof HTMLFormElement)) return null;
  const nested = form.querySelector('button[name="checkout"]');
  if (nested instanceof HTMLButtonElement) return nested;
  const fid = form.getAttribute('id');
  if (fid) {
    const associated = document.querySelector(`button[name="checkout"][form="${fid}"]`);
    if (associated instanceof HTMLButtonElement) return associated;
  }
  const byId = document.getElementById('checkout');
  if (byId instanceof HTMLButtonElement && byId.getAttribute('name') === 'checkout') return byId;
  return null;
}

/** Single debounced cart:update — avoids N timers when header + drawer each mount <cart-progress>. */
let cartProgressSharedUpdateRefCount = 0;
let cartProgressSharedDebounceTimer = null;
/** Only one <cart-progress> may run /cart/add|change|dedupe (prevents races between header + drawer). */
let cartProgressGlobalGiftMutationLock = false;

/**
 * Which element runs gift mutations: drawer first (same UX as minicart), else first bar.
 * @returns {Element | null}
 */
function cartProgressGiftDriver() {
  return (
    document.querySelector('cart-drawer-component cart-progress') ||
    document.querySelector('.cart-drawer cart-progress') ||
    document.querySelector('cart-progress')
  );
}

function cartProgressIsCartLike(value) {
  return (
    value != null &&
    typeof value === 'object' &&
    typeof value.total_price === 'number' &&
    Array.isArray(value.items)
  );
}

/**
 * One debounced handler for all <cart-progress> instances (header + drawer
 * used to register separate listeners → duplicate gift API calls).
 */
function cartProgressSharedCartUpdateHandler(event) {
  const detail = event && event.detail;
  const meta = detail && detail.data;
  if (meta && meta.source === 'cart-progress') return;

  const candidates = detail
    ? [detail.resource, detail.cart, meta, meta?.resource, meta?.cart]
    : [];
  let piggybackCart = null;
  for (const c of candidates) {
    if (cartProgressIsCartLike(c)) {
      piggybackCart = c;
      break;
    }
  }

  clearTimeout(cartProgressSharedDebounceTimer);
  cartProgressSharedDebounceTimer = setTimeout(async () => {
    let cart = piggybackCart;
    if (!cartProgressIsCartLike(cart)) {
      try {
        const res = await fetch('/cart.js');
        if (res.ok) cart = await res.json();
      } catch (_) {
        return;
      }
    }
    if (!cartProgressIsCartLike(cart)) return;
    document.querySelectorAll('cart-progress').forEach((el) => {
      if (typeof el.animateCart === 'function') {
        el.animateCart(true, cart);
      }
    });
  }, 150);
}

/**
 * @param {SubmitEvent} event
 */
async function cartProgressOnDocumentCartSubmitCapture(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.id !== 'cart-form' && !form.classList.contains('cart-form')) return;

  const sub = event.submitter;
  if (!(sub instanceof HTMLButtonElement) || sub.name !== 'checkout') return;
  if (sub.disabled || sub.getAttribute('aria-disabled') === 'true') return;

  if (form.dataset.gwpCheckoutSyncPass === '1') {
    delete form.dataset.gwpCheckoutSyncPass;
    return;
  }

  const host = /** @type {any} */ (cartProgressGiftDriver());
  if (!host?.querySelector?.('.ns-target-gift.noappgift')) return;
  if (typeof host.syncGiftsBeforeCheckout !== 'function') return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const wasDisabled = sub.disabled;
  sub.disabled = true;
  try {
    await host.syncGiftsBeforeCheckout();
  } catch (err) {
    console.error('cart-progress: pre-checkout GWP sync failed', err);
  } finally {
    sub.disabled = wasDisabled;
  }

  const formAfter = document.getElementById('cart-form');
  const subAfter = cartProgressFindCheckoutButton(formAfter);
  if (!(formAfter instanceof HTMLFormElement) || !(subAfter instanceof HTMLButtonElement)) {
    console.warn('cart-progress: cart form or checkout button missing after GWP sync');
    return;
  }
  formAfter.dataset.gwpCheckoutSyncPass = '1';
  formAfter.requestSubmit(subAfter);
}

if (!window.customElements.get('cart-progress')) {
  window.customElements.define(
    'cart-progress',
    class CartProgress extends HTMLElement {
      constructor() {
        super();
        this.debounceTimeout = null;
        this._lastCart = null;
        this._giftLock = false;
      }

      /** Narrow-type check: does this object look like a Shopify cart JSON? */
      #isCartLike(value) {
        return (
          value != null &&
          typeof value === 'object' &&
          typeof value.total_price === 'number' &&
          Array.isArray(value.items)
        );
      }

      /** Line added by this script — safe to remove when cart drops below threshold. */
      #lineItemIsThemeAutoGift(item) {
        if (!item || item.properties == null) return false;
        const p = item.properties;
        const v = p[GWP_AUTO_PROPERTY] ?? p._saltlab_gwp;
        return String(v ?? '') === GWP_AUTO_VALUE;
      }

      /**
       * Exclude this line from qualifying spend when it is the promo gift
       * (theme-managed) or a fully discounted gift line (e.g. BXGY $0).
       * Purchased same-variant lines stay in the qualifying total.
       */
      #lineItemExcludesFromQualifyingSpend(item, giftIdStr) {
        if (String(item.variant_id) !== giftIdStr) return false;
        if (this.#lineItemIsThemeAutoGift(item)) return true;
        if (typeof item.final_line_price === 'number' && item.final_line_price === 0) return true;
        return false;
      }

      /**
       * Spend used for free-gift milestones — cart total minus:
       * - shipping protection lines (if configured)
       * - promo gift lines only (theme `_saltlab_gwp` lines + $0 gift lines),
       *   not customer-purchased units of the same SKU
       */
      #qualifyingCartTotal(cart) {
        if (!cart || typeof cart.total_price !== 'number') return 0;
        let total = cart.total_price;

        const rawPid = this.dataset.shippingProtectionProductId;
        const protId = rawPid ? parseInt(rawPid, 10) : NaN;

        const giftVariantIds = new Set();
        this.querySelectorAll('.ns-target-gift').forEach((g) => {
          const raw = g.dataset.giftId;
          if (raw != null && String(raw).trim() !== '') {
            giftVariantIds.add(String(raw));
          }
        });

        for (const item of cart.items || []) {
          if (!Number.isNaN(protId) && item.product_id === protId) {
            total -= item.final_line_price;
            continue;
          }
          const vid = String(item.variant_id);
          if (giftVariantIds.has(vid) && this.#lineItemExcludesFromQualifyingSpend(item, vid)) {
            total -= item.final_line_price;
          }
        }
        return Math.max(0, total);
      }

      connectedCallback() {
        this.animateCart(false);
        if (this.querySelectorAll('.ns-target-gift').length > 0) {
          queueMicrotask(() => this.animateCart(true));
        }

        if (cartProgressSharedUpdateRefCount === 0) {
          document.addEventListener('cart:update', cartProgressSharedCartUpdateHandler);
        }
        cartProgressSharedUpdateRefCount += 1;

        if (!cartProgressCheckoutHookInstalled) {
          cartProgressCheckoutHookInstalled = true;
          document.addEventListener('submit', cartProgressOnDocumentCartSubmitCapture, true);
        }
      }

      disconnectedCallback() {
        clearTimeout(this.debounceTimeout);
        cartProgressSharedUpdateRefCount -= 1;
        if (cartProgressSharedUpdateRefCount <= 0) {
          cartProgressSharedUpdateRefCount = 0;
          clearTimeout(cartProgressSharedDebounceTimer);
          cartProgressSharedDebounceTimer = null;
          document.removeEventListener('cart:update', cartProgressSharedCartUpdateHandler);
        }
      }

      #syncMilestoneTicks(milestoneCount) {
        const host = this.querySelector('.ns-progress-ticks');
        if (!host) return;
        host.innerHTML = '';
        if (milestoneCount < 2) return;
        for (let i = 1; i < milestoneCount; i++) {
          const tick = document.createElement('span');
          tick.className = 'ns-progress-tick';
          tick.style.left = `${(100 / milestoneCount) * i}%`;
          host.appendChild(tick);
        }
      }

      #sortedMilestoneThresholds() {
        const gifts = this.querySelectorAll('.ns-target-gift');
        const set = new Set();
        for (const g of gifts) {
          const t = parseFloat(g.dataset.giftTarget);
          if (!Number.isNaN(t) && t > 0) set.add(t);
        }
        return [...set].sort((a, b) => a - b);
      }

      #milestoneBarPercent(cartTotalCents, thresholds) {
        const n = thresholds.length;
        if (n === 0) return null;

        const last = thresholds[n - 1];
        if (cartTotalCents >= last) return 100;

        const segmentWidth = 100 / n;

        for (let k = 0; k < n; k++) {
          const prev = k === 0 ? 0 : thresholds[k - 1];
          const next = thresholds[k];
          if (cartTotalCents < next) {
            const within = (cartTotalCents - prev) / (next - prev);
            return k * segmentWidth + within * segmentWidth;
          }
        }
        return 100;
      }

      async fetchCart() {
        try {
          const res = await fetch('/cart.js');
          if (!res.ok) throw new Error('Cart fetch failed');
          return await res.json();
        } catch (error) {
          console.error('Error fetching cart:', error);
          return null;
        }
      }

      /**
       * Await before navigating to checkout: runs the same gift add/remove/dedupe
       * logic as a cart update, without the debounce. Used so fast checkout clicks
       * do not skip the GWP line while the drawer is still catching up.
       */
      async syncGiftsBeforeCheckout() {
        if (cartProgressGiftDriver() !== this) return;
        const cart = await this.fetchCart();
        if (!this.#isCartLike(cart)) return;
        await this.animateCart(true, cart);
      }

      #getCartSectionIds() {
        return Array.from(document.querySelectorAll('cart-items-component'))
          .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
          .filter(Boolean);
      }

      /**
       * When the same gift variant appears as multiple line items (common if
       * theme /cart/add.js and Shopify BXGY both inject the gift), collect
       * line keys to remove — keep one line, preferring $0 / most-discounted.
       */
      #collectGiftDedupeLineKeys(cart, giftBlocks) {
        const keySet = new Set();
        const processedVariants = new Set();
        for (const gift of giftBlocks) {
          const giftIdRaw = gift.dataset.giftId;
          if (giftIdRaw == null || String(giftIdRaw).trim() === '') continue;
          if (!gift.classList.contains('noappgift')) continue;
          const giftIdStr = String(giftIdRaw);
          if (processedVariants.has(giftIdStr)) continue;
          processedVariants.add(giftIdStr);
          const lines = (cart.items || []).filter((item) => String(item.variant_id) === giftIdStr);
          const managed = lines.filter(
            (li) =>
              this.#lineItemIsThemeAutoGift(li) ||
              (typeof li.final_line_price === 'number' && li.final_line_price === 0)
          );
          if (managed.length <= 1) continue;
          const sorted = [...managed].sort((a, b) => {
            const aFree = a.final_line_price === 0;
            const bFree = b.final_line_price === 0;
            if (aFree && !bFree) return -1;
            if (bFree && !aFree) return 1;
            return a.final_line_price - b.final_line_price;
          });
          sorted.slice(1).forEach((li) => {
            if (li.key) keySet.add(li.key);
          });
        }
        return [...keySet];
      }

      async #removeCartLineKeys(lineKeys, sectionIds) {
        let lastJson = null;
        for (const key of lineKeys) {
          const body = { id: key, quantity: 0 };
          if (sectionIds.length) body.sections = sectionIds.join(',');
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          lastJson = await res.json();
          if (!res.ok) {
            console.error('Cart progress: cart/change.js dedupe failed', lastJson);
          }
        }
        if (lastJson && typeof lastJson.total_price === 'number') return lastJson;
        return this.fetchCart();
      }

      updateFreeShippingMessage(cartTotal, freeShippingThreshold) {
        const freeShippingMessage = this.querySelector('.free-shipping-message');
        if (!freeShippingMessage || !freeShippingThreshold) return;

        const freeShippingGap = Math.max(0, freeShippingThreshold - cartTotal);
        const messageText = freeShippingMessage.querySelector('.free-shipping-text');

        if (!messageText) return;

        if (freeShippingGap > 0) {
          const gapInDollars = Math.round(freeShippingGap / 100);
          messageText.textContent = `You're $${gapInDollars} away from free shipping!`;
          messageText.classList.remove('free-shipping-achieved');
        } else {
          messageText.textContent = 'Congrats! Your order qualifies for free shipping.';
          messageText.classList.add('free-shipping-achieved');
        }
      }

      async animateCart(fromEvent, preFetchedCart = null) {
        const progressBar = this.querySelector('.ns-progress');
        const progressFill = this.querySelector('.ns-progress-fill');
        let cart = null;

        const validPiggyback = this.#isCartLike(preFetchedCart) ? preFetchedCart : null;

        if (fromEvent) {
          // Reuse a cart passed in via the triggering event when it's a
          // real cart payload; otherwise go fetch a fresh one.
          cart = validPiggyback || (await this.fetchCart());
          if (this.#isCartLike(cart)) this._lastCart = cart;
        } else if (validPiggyback) {
          cart = validPiggyback;
          this._lastCart = cart;
        }

        const cartTotal = fromEvent
          ? this.#qualifyingCartTotal(cart)
          : parseFloat(progressBar?.dataset.progress || 0);

        const giftBlocks = Array.from(this.querySelectorAll('.ns-target-gift'));
        const milestones = this.#sortedMilestoneThresholds();
        this.#syncMilestoneTicks(giftBlocks.length);

        const freeShippingMessage = this.querySelector('.free-shipping-message');
        if (giftBlocks.length > 0 && freeShippingMessage) {
          freeShippingMessage.style.display = 'none';
        } else if (freeShippingMessage) {
          freeShippingMessage.style.display = '';
          const freeShippingThreshold = parseFloat(freeShippingMessage.dataset.freeShippingThreshold || 0);
          this.updateFreeShippingMessage(cartTotal, freeShippingThreshold);
        }

        const targetGiftsContainer = this.querySelector('.target-gifts');

        if (targetGiftsContainer && giftBlocks.length > 0) {
          giftBlocks.forEach((gift) => {
            if (!targetGiftsContainer.contains(gift)) {
              targetGiftsContainer.appendChild(gift);
            }
          });

          targetGiftsContainer.classList.remove('size-1', 'size-2', 'size-3', 'size-4', 'size-5', 'size-6');
          targetGiftsContainer.classList.add(`size-${giftBlocks.length}`);
        }

        const locks = this.querySelectorAll('.target-locks .target');
        locks.forEach((lock) => {
          const targetVal = parseFloat(lock.dataset.target);
          if (!targetVal || targetVal > 999990) return;
          if (cartTotal >= targetVal) {
            lock.classList.add('active');
          } else {
            lock.classList.remove('active');
          }
        });

        if (progressFill) {
          let pct = null;
          if (milestones.length > 0) {
            pct = this.#milestoneBarPercent(cartTotal, milestones);
          } else if (freeShippingMessage) {
            const freeShippingThreshold = parseFloat(freeShippingMessage.dataset.freeShippingThreshold || 0);
            if (freeShippingThreshold > 0) {
              pct = Math.min(100, (cartTotal / freeShippingThreshold) * 100);
            }
          }
          if (pct !== null) {
            progressFill.style.width = `${pct}%`;
          }
        }

        const giftDriver = cartProgressGiftDriver();
        const runGiftMutations = giftDriver === this;

        if (cart && giftBlocks.length > 0 && runGiftMutations && !cartProgressGlobalGiftMutationLock) {
          cartProgressGlobalGiftMutationLock = true;
          try {
            const sectionIds = this.#getCartSectionIds();
            const dedupeKeys = this.#collectGiftDedupeLineKeys(cart, giftBlocks);
            if (dedupeKeys.length > 0) {
              const refreshed = await this.#removeCartLineKeys(dedupeKeys, sectionIds);
              if (this.#isCartLike(refreshed)) {
                cart = refreshed;
                this._lastCart = cart;
              }
              const dedupeSections =
                refreshed && typeof refreshed === 'object' && refreshed.sections
                  ? refreshed.sections
                  : null;
              document.dispatchEvent(
                new CartUpdateEvent(cart, 'cart-progress', {
                  itemCount: cart?.item_count ?? 0,
                  source: 'cart-progress',
                  ...(dedupeSections ? { sections: dedupeSections } : {}),
                })
              );
            }

            const qualifyingTotal = this.#qualifyingCartTotal(cart);
            const toAdd = [];
            const toRemoveLineKeys = [];

            for (const gift of giftBlocks) {
            const giftTarget = parseFloat(gift.dataset.giftTarget);
            const giftIdRaw = gift.dataset.giftId;
            const hasVariant = giftIdRaw != null && String(giftIdRaw).trim() !== '';
            const giftIdStr = hasVariant ? String(giftIdRaw) : '';
            const { classList } = gift;

            const giftLines = hasVariant
              ? (cart.items || []).filter((item) => String(item.variant_id) === giftIdStr)
              : [];
            const autoGiftLines = giftLines.filter((li) => this.#lineItemIsThemeAutoGift(li));
            const autoGiftQty = autoGiftLines.reduce((sum, li) => sum + (li.quantity || 0), 0);
            const giftQty = giftLines.reduce((sum, li) => sum + (li.quantity || 0), 0);
            const hasGift = giftQty > 0;
            const hasFreeGiftLine = giftLines.some(
              (li) => typeof li.final_line_price === 'number' && li.final_line_price === 0
            );
            const showGiftAdded = autoGiftQty > 0 || hasFreeGiftLine;
            const noAppGift = classList.contains('noappgift');
            const isMultiGift = classList.contains('multigift');
            const qualified = qualifyingTotal >= giftTarget;

            const giftAddedEl = gift.querySelector('[data-gift-added-message]');
            const giftNotAddedEl = gift.querySelector('[data-gift-not-added-message]');
            if (hasVariant) {
              if (giftAddedEl) giftAddedEl.style.display = showGiftAdded ? 'inline' : 'none';
              if (giftNotAddedEl) giftNotAddedEl.style.display = showGiftAdded ? 'none' : 'inline';
            } else {
              if (giftAddedEl) giftAddedEl.style.display = qualified ? 'inline' : 'none';
              if (giftNotAddedEl) giftNotAddedEl.style.display = qualified ? 'none' : 'inline';
            }

            if (!hasVariant) continue;

            const vid = parseInt(giftIdStr, 10);
            if (Number.isNaN(vid)) continue;

            if (qualified && autoGiftQty === 0 && !hasFreeGiftLine && noAppGift) {
              toAdd.push({
                id: vid,
                quantity: 1,
                properties: { [GWP_AUTO_PROPERTY]: GWP_AUTO_VALUE },
              });
            } else if (qualified && autoGiftQty > 1 && noAppGift) {
              const sorted = [...autoGiftLines].sort((a, b) => {
                const aFree = a.final_line_price === 0;
                const bFree = b.final_line_price === 0;
                if (aFree && !bFree) return -1;
                if (bFree && !aFree) return 1;
                return a.final_line_price - b.final_line_price;
              });
              sorted.slice(1).forEach((li) => {
                if (li.key) toRemoveLineKeys.push(li.key);
              });
            } else if (!qualified && autoGiftQty > 0 && noAppGift) {
              autoGiftLines.forEach((li) => {
                if (li.key) toRemoveLineKeys.push(li.key);
              });
            } else if (isMultiGift && !hasGift) {
              toAdd.push({
                id: vid,
                quantity: 1,
                properties: { [GWP_AUTO_PROPERTY]: GWP_AUTO_VALUE },
              });
            }
          }

          const uniqueRemoveKeys = [...new Set(toRemoveLineKeys)];

            if (toAdd.length > 0 || uniqueRemoveKeys.length > 0) {
              await this.#applyGiftChanges(toAdd, uniqueRemoveKeys);
            }
          } finally {
            cartProgressGlobalGiftMutationLock = false;
          }
        }
      }

      async #applyGiftChanges(toAdd, removeLineKeys) {
        const _giftLabel = toAdd.length > 0 && removeLineKeys.length > 0
          ? '[Cart] free gift add + remove'
          : toAdd.length > 0
            ? '[Cart] free gift add'
            : '[Cart] free gift remove';
        console.time(_giftLabel);

        const sectionIds = this.#getCartSectionIds();

        let sections = null;
        let freshCart = null;

        try {
          if (removeLineKeys.length > 0) {
            freshCart = await this.#removeCartLineKeys(removeLineKeys, sectionIds);
            if (freshCart && typeof freshCart === 'object' && freshCart.sections) {
              sections = freshCart.sections;
            }
          }

          if (toAdd.length > 0) {
            const payload = { items: toAdd };
            if (sectionIds.length) payload.sections = sectionIds.join(',');
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(payload),
            });
            const json = await res.json();
            if (!res.ok) {
              console.error('Cart progress: cart/add.js failed', json);
            } else if (json.sections) {
              sections = json.sections;
            }
            // /cart/add.js returns only the added items, not the full cart —
            // we still need one /cart.js fetch after an add.
          }

          if (toAdd.length > 0 || removeLineKeys.length > 0) {
            freshCart = await this.fetchCart();
          } else if (!this.#isCartLike(freshCart)) {
            freshCart = await this.fetchCart();
          }

          this._lastCart = freshCart;
          document.dispatchEvent(
            new CartUpdateEvent(freshCart, 'cart-progress', {
              itemCount: freshCart?.item_count ?? 0,
              source: 'cart-progress',
              ...(sections ? { sections } : {}),
            })
          );
        } catch (err) {
          console.error('Cart update error:', err);
        } finally {
          console.timeEnd(_giftLabel);
        }
      }
    }
  );
}
